import classNames from "classnames";
import PropTypes from "prop-types";
import qs from "qs";
import React, { lazy, Suspense } from "react";
import { withRouter } from "react-router";
import { Col, Container, Row } from "reactstrap";

import { UrlProvider } from "../../api/UrlProvider";
import { isSafeAuthRedirectUrl } from "../../lib/auth/authRedirectUrl";
import withDeviceType from "../../hocs/withDeviceType";
import withExecuteAuthRedirect from "../../hocs/withExecuteAuthRedirect";
import withOverlay from "../../hocs/withOverlay";
import withUserData from "../../hocs/withUserData";
import PageHelper from "../../lib/PageHelper";
import { getIsMobile } from "../../lib/platformHelper";
import Loader from "./common/Loader";
import withFullPageLoader from "../../hocs/withFullPageLoader";
import withStaticPageLayout from "../../hocs/withStaticPageLayout";

const Header = lazy(() => import("./navigation/Header"));
// prettier-ignore
const MobileNotificationList = lazy(() => import("./navigation/MobileNotificationList"));
// prettier-ignore
const MobilePortfoliosList = lazy(() => import("./navigation/MobilePortfoliosList"));
// prettier-ignore
const MobileFollowedStocksList = lazy(() => import("./common/MobileFollowedStocksList"));
const MobileUserMenu = lazy(() => import("./common/MobileUserMenu"));
const Footer = lazy(() => import("./common/Footer/"));
const MobileNavigation = lazy(() => import("./navigation/MobileNavigation"));

@withRouter
@withUserData
@withExecuteAuthRedirect
@withDeviceType
@withOverlay
@withFullPageLoader
@withStaticPageLayout
class Template extends React.Component {
  static propTypes = { children: PropTypes.element.isRequired };

  componentDidMount(): void {
    const {
      location,
      executeAuthRedirect,
      userData,
      history,
      locale
    } = this.props;

    if (location.search) {
      const urlParams = qs.parse(window.location.search, {
        ignoreQueryPrefix: true
      });

      if (
        urlParams.authRedirect &&
        isSafeAuthRedirectUrl(urlParams.authRedirect)
      ) {
        if (userData) {
          executeAuthRedirect(urlParams.authRedirect);
        } else {
          history.push(
            UrlProvider.getUrl("fe.login", {
              locale
            }) + `?authRedirect=${encodeURIComponent(urlParams.authRedirect)}`
          );
        }
      }
    }
  }

  render() {
    const {
      children,
      location,
      locale,
      isDeviceMobile,
      userData,
      fullPageLoader,
      staticPageLayout,
      shouldHideStaticPageMenuAndSearch
    } = this.props;

    const isRegisterPage =
      PageHelper.getIsRegisterPage(location, locale) ||
      PageHelper.getIsLoginOtpPage(location, locale);
    const isAuthPage = PageHelper.getIsAuthPage(location, locale);
    const isHomePage = PageHelper.getIsHomePage(location, locale);
    const isOfferCheckoutPage = PageHelper.getIsOfferCheckoutPage(
      location,
      locale
    );
    const isLandingPage = isHomePage || PageHelper.getIsLandingPage(location);
    const isMobile = getIsMobile() || isDeviceMobile;
    const fullWidth = !!staticPageLayout?.fullWidth;

    const canDisplayVerifyEmailBar =
      isMobile && userData && !userData.email_validated;

    const shouldRenderRegularAppContent =
      !isAuthPage &&
      !isHomePage &&
      !isLandingPage &&
      !isOfferCheckoutPage &&
      !shouldHideStaticPageMenuAndSearch;

    const shouldRenderFooter =
      !isLandingPage && !isOfferCheckoutPage && !isAuthPage;

    return (
      <React.Fragment>
        {shouldRenderRegularAppContent ? (
          <Suspense fallback={<div />}>
            <Header />
          </Suspense>
        ) : null}
        <Container
          className={classNames({
            "app-inside": true,
            "is-register-page": isRegisterPage,
            "is-landing-page": isLandingPage,
            "is-login-page": isAuthPage,
            "is-full-width-static": fullWidth
          })}
        >
          {shouldRenderRegularAppContent && isMobile ? (
            <Suspense fallback={<div />}>
              <MobileFollowedStocksList
                canDisplayVerifyEmailBar={canDisplayVerifyEmailBar}
              />
              <MobileNotificationList
                canDisplayVerifyEmailBar={canDisplayVerifyEmailBar}
              />
              <MobileUserMenu />
              <MobilePortfoliosList
                canDisplayVerifyEmailBar={canDisplayVerifyEmailBar}
              />
            </Suspense>
          ) : null}
          <Row>
            <Col sm="12">{fullPageLoader ? <Loader /> : children}</Col>
          </Row>
        </Container>
        {shouldRenderRegularAppContent ? (
          <Suspense fallback={<div />}>
            <MobileNavigation />
          </Suspense>
        ) : null}
        {shouldRenderFooter ? (
          <Suspense fallback={<div />}>
            <Footer />
          </Suspense>
        ) : null}
      </React.Fragment>
    );
  }
}

export default Template;
